import { HistoryOfferItem } from 'client-code/recordings';

import { Locale, LocaleKeys } from '../../../../core/locale';
import { Log } from '../../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../../core/navigation';
import {
    getHistoryItemsFromPage,
    getRecordingItemsFromPage,
    LIST_ITEMS_MAX_COUNT,
    LIST_ROWS_ON_PAGE,
} from '../../../../views/SettingsScreen/Manage/RecordingActivity/RecordingActivity.utils';
import {
    onKeyBackNoNetworkOverlay,
    onKeyOkNoNetworkOverlay,
} from '../../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import { resetPCPinValuesAction } from '../../../overlay/pcpinoverlay/pcpinoverlay.actions';
import {
    hidePcPinOverlay,
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
} from '../../../overlaydialog/overlaydialog.reducers';
import { showGuideScreen, showPreviousScreen } from '../../../screens/screens.reducers';
import {
    goToRecordingActivityNextPageAction,
    goToRecordingActivityPreviousPageAction,
} from '../recordingactivity.actions';
import {
    handleRecordingActivityErrorOrNetwork,
    onKeyOkNoTaskRecordingActivity,
    resetRecordingActivityState,
    showHistoryConflictOverlay,
} from '../recordingactivity.reducers';
import {
    onKeyDownRecordingActivityAction,
    onKeyUpRecordingActivityAction,
} from './recordingactivity.navigation.actions';

const TAG = 'RecordingActivityNavigationReducers';

export const onKeyBackRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(
                resetRecordingActivityState({
                    resetError: true,
                    resetCounter: true,
                }),
            );
            dispatch(showPreviousScreen());
            return;

        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyBackNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        default:
    }
};

export const onKeyOkRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { isError } = getState().recordingActivity;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;
            dispatch(onKeyOkNoTaskRecordingActivity());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onKeyOkNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY: {
            const { verticalPosition } = getState().overlayHighlight;
            const { uiProperties } = getState().overlayDialog;
            const { options } = uiProperties;
            const selectedOption = options[verticalPosition];

            if (
                selectedOption.text ===
                Locale.getString(LocaleKeys.RECORDING_ACTIVITY_GO_TO_RECOVER)
            ) {
                dispatch(hideOverlayDialog());
                dispatch(onKeyOkNoTaskRecordingActivity(true));
                return;
            }
            dispatch(hideOverlayDialog());
            break;
        }
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        default:
    }
};

export const onKeyGuideRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(showGuideScreen());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(resetPCPinValuesAction());
            dispatch(hidePcPinOverlay());
            break;
        default:
    }
};

export const onKeyDigitRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};

export const onKeyUpRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, currentPage, recordingItems, isError } = getState().recordingActivity;
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.NO_TASK: {
            if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

            if (verticalPosition <= 0) {
                if (validateCanGoToPreviousPageRecordingActivity(currentPage)) {
                    const { actualItemsLength } = getRecordingItemsFromPage(
                        recordingItems,
                        currentPage - 1,
                    );
                    dispatch(goToRecordingActivityPreviousPageAction(actualItemsLength - 1));
                }
            } else {
                dispatch(onKeyUpRecordingActivityAction(verticalPosition - 1));
            }
            break;
        }
        default:
    }
};

export const onKeyDownRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, currentPage, recordingItems, totalCount, isError } =
        getState().recordingActivity;
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.NO_TASK: {
            if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;
            const { actualItemsLength } = getRecordingItemsFromPage(recordingItems, currentPage);

            if (verticalPosition < actualItemsLength - 1) {
                dispatch(onKeyDownRecordingActivityAction(verticalPosition + 1));
                return;
            }

            if (validateCanGoToNextPageRecordingActivity(recordingItems, currentPage, totalCount)) {
                dispatch(goToRecordingActivityNextPageAction());
            }
            break;
        }
        default:
    }
};

export const onKeyChannelUpRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { currentPage, recordingItems, isError } = getState().recordingActivity;
    const { screenTaskController } = getState().screens;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }

    if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

    if (validateCanGoToPreviousPageRecordingActivity(currentPage)) {
        const { actualItemsLength } = getRecordingItemsFromPage(recordingItems, currentPage - 1);
        dispatch(goToRecordingActivityPreviousPageAction(actualItemsLength - 1));
    }
};

export const onKeyChannelDownRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { currentPage, recordingItems, totalCount, isError } = getState().recordingActivity;
    const { screenTaskController } = getState().screens;

    if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
        return;
    }

    if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

    if (validateCanGoToNextPageRecordingActivity(recordingItems, currentPage, totalCount)) {
        dispatch(goToRecordingActivityNextPageAction());
    }
};

export const onKeyLeftRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        default:
    }
};

export const onKeyRightRecordingActivity = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        default:
    }
};

export const validateCanGoToPreviousPageRecordingActivity = (currentPage: number) => {
    if (currentPage > 0) {
        return true;
    }
    Log.i(TAG, 'Already on the first page. Action is ignored.');
    return false;
};

export const validateCanGoToNextPageRecordingActivity = (
    recordingItems: HistoryOfferItem[],
    currentPage: number,
    totalCount: number,
) => {
    const finalPage = Math.ceil(totalCount / LIST_ROWS_ON_PAGE) - 1;
    const hardFinalPage = Math.ceil(LIST_ITEMS_MAX_COUNT / LIST_ROWS_ON_PAGE) - 1;

    if (currentPage === finalPage || currentPage === hardFinalPage) {
        Log.i(TAG, 'Already on the last page. Action is ignored.');
        return false;
    }
    const hasItemsOnThisPage = recordingItems.length > currentPage * LIST_ROWS_ON_PAGE;
    if (!hasItemsOnThisPage) {
        Log.i(TAG, 'User is currently waiting for items to load. Action is ignored.');
        return false;
    }
    return true;
};

export const onKeyInfoRecordingActivity = () => (dispatch: Function) => {
    dispatch(
        onOpenConflictOverlayRecordingActivity('No current item found. Info Action is ignored.'),
    );
};

export const onLongPressOkRecordingActivity = () => (dispatch: Function) => {
    dispatch(
        onOpenConflictOverlayRecordingActivity(
            'No current item found. Long OK/SELECT Action is ignored.',
        ),
    );
};

export const onMouseWheelRecordingActivity =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.NO_TASK:
                if (wheelDeltaY < 0) {
                    dispatch(onKeyChannelDownRecordingActivity());
                } else if (wheelDeltaY > 0) {
                    dispatch(onKeyChannelUpRecordingActivity());
                }
                break;
            default:
        }
    };

const onOpenConflictOverlayRecordingActivity =
    (logMessage: string) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        const { verticalPosition, currentPage, recordingItems, isError } =
            getState().recordingActivity;

        if (screenTaskController !== ScreenTaskControllerTypes.NO_TASK) {
            return;
        }

        if (handleRecordingActivityErrorOrNetwork(dispatch, isError)) return;

        const { actualItems } = getHistoryItemsFromPage(recordingItems, currentPage);
        const currentItem = actualItems?.[verticalPosition];

        if (!currentItem) {
            Log.w(TAG, logMessage);
            return;
        }
        dispatch(showHistoryConflictOverlay(currentItem));
    };
